import React from 'react';
import { logger } from '@/utils/logger';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCartStore } from '@/store/cartStore';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { supabase } from '@/lib/supabaseClient';
import {
  DeliverySlot,
  getNextAvailableDeliverySlot
} from '@/lib/deliveryUtils';
import { createOrderDirect, testCreateOrderFunction } from '@/api/orders';
import { getAppSettings, PaymentMethod } from '@/api/settings';
import { DelayedOrderPaymentForm } from '@/components/checkout/DelayedOrderPaymentForm';
import { PaymentStatus } from '@/components/checkout/PaymentStatus';

interface GuestDetails {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
}

// AutomaticDeliveryInfo component - displays automatically assigned delivery info
function AutomaticDeliveryInfo({
  onDeliverySlotAssigned
}: {
  onDeliverySlotAssigned: (date: Date, slot: DeliverySlot) => void;
}) {
  const [assignedSlot, setAssignedSlot] = React.useState<DeliverySlot | null>(null);
  const [assignedDate, setAssignedDate] = React.useState<Date | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [hasAssigned, setHasAssigned] = React.useState(false);

  // Automatically assign the next available delivery slot
  React.useEffect(() => {
    // Prevent multiple calls
    if (hasAssigned) return;

    async function assignDeliverySlot() {
      try {
        setIsLoading(true);
        setError(null);
        
        const nextAvailable = await getNextAvailableDeliverySlot();
        
        if (nextAvailable) {
          setAssignedDate(nextAvailable.date);
          setAssignedSlot(nextAvailable.slot);
          setHasAssigned(true);
          onDeliverySlotAssigned(nextAvailable.date, nextAvailable.slot);
        } else {
          setError('Inga leveranstider är tillgängliga för tillfället. Vänligen kontakta kundtjänst.');
        }
      } catch (error) {
        logger.error('Error assigning delivery slot:', error);
        setError('Kunde inte tilldela leveranstid. Vänligen försök igen senare.');
      } finally {
        setIsLoading(false);
      }
    }

    assignDeliverySlot();
  }, [onDeliverySlotAssigned, hasAssigned]);

  if (isLoading) {
    return (
      <div className="py-4 text-center">
        <p className="text-gray-500">Tilldelar leveranstid...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-red-500">{error}</p>
        <p className="text-sm text-gray-500 mt-2">
          Om problemet kvarstår, vänligen kontakta vår kundtjänst.
        </p>
      </div>
    );
  }

  if (assignedDate && assignedSlot) {
    return (
      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">
              Leveranstid och datum {format(assignedDate, 'EEEE d MMMM yyyy', { locale: sv })}
            </h3>
            <div className="mt-1 text-sm text-green-700">
              <p>
                Klockan {assignedSlot.start_time.slice(0, 5)} - {assignedSlot.end_time.slice(0, 5)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export function Checkout() {
  const { user } = useAuth();
  const { items, clearCart, total } = useCartStore();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = React.useState<DeliverySlot | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([]);
  const [paymentMethodsText, setPaymentMethodsText] = React.useState('');
  const [minOrderAmount, setMinOrderAmount] = React.useState(0);
  const [isLoadingSettings, setIsLoadingSettings] = React.useState(true);

  // Payment state
  const [showPaymentForm, setShowPaymentForm] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState<string | null>(null);
  const [isPollingForOrder, setIsPollingForOrder] = React.useState(false);
  // T008: Removed orderDataForPayment - order data should only exist in payment_authorizations metadata
  
  const [formData, setFormData] = React.useState({
    firstName: user?.user_metadata?.first_name || '',
    lastName: user?.user_metadata?.last_name || '',
    email: user?.email || '',
    phone: user?.user_metadata?.phone || '',
    streetAddress: user?.user_metadata?.address || '',
    postalCode: user?.user_metadata?.postal_code || '',
    city: user?.user_metadata?.city || '',
    notes: '',
    dataConsent: false,
    marketingConsent: false,
  });

  // Calculate the cart total
  const cartTotal = total();
  
  // Check if order meets minimum amount requirement
  const isBelowMinimumOrder = minOrderAmount > 0 && cartTotal < minOrderAmount;

  // Fetch payment methods and minimum order amount on load
  React.useEffect(() => {
    async function fetchPaymentMethodsData() {
      try {
        setIsLoadingSettings(true);
        const settings = await getAppSettings();
        setPaymentMethods(settings.paymentMethods.filter(method => method.enabled) || []);
        setPaymentMethodsText(settings.paymentMethodsText || '');
        setMinOrderAmount(settings.minOrderAmount || 0);
      } catch (error) {
        logger.error('Error loading settings:', error);
      } finally {
        setIsLoadingSettings(false);
      }
    }

    fetchPaymentMethodsData();
  }, []);

  React.useEffect(() => {
    // Only redirect if we're on the checkout page (not order confirmation)
    if (window.location.pathname === '/checkout') {
      // Check if cart is empty or below minimum order amount, redirect to cart page
      if (items.length === 0 || (minOrderAmount > 0 && cartTotal < minOrderAmount && !isLoadingSettings)) {
        navigate('/cart');
      }
    }
  }, [items.length, navigate, minOrderAmount, cartTotal, isLoadingSettings]);

  // Handle automatic delivery slot assignment
  const handleDeliverySlotAssigned = React.useCallback((date: Date, slot: DeliverySlot) => {
    setSelectedDate(date);
    setSelectedSlot(slot);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setPaymentError(null);

    // Double-check minimum order amount before proceeding
    if (minOrderAmount > 0 && cartTotal < minOrderAmount) {
      setError(`Din beställning måste vara minst ${minOrderAmount} kr för att fortsätta.`);
      return;
    }

    setLoading(true);

    try {
      // Get current user ID if logged in
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const userId = currentUser?.id;

      // Prepare order items with proper price validation
      const orderItems = items.map(item => {
        // Ensure price is a valid number
        const price = Number(item.price);
        if (isNaN(price) || price <= 0) {
          logger.warn(`Invalid price for item: ${item.id}`, item.price);
        }

        return {
          product_id: item.id,
          quantity: item.quantity,
          unit_price: price > 0 ? price : 0
        };
      });

      // Calculate and validate total
      const calculatedTotal = total();
      if (calculatedTotal <= 0) {
        logger.error('Cart total is zero or negative:', calculatedTotal);
        throw new Error('Order total cannot be zero or negative. Please check your cart items and try again.');
      }

      // Prepare order data for payment (order will be created AFTER payment succeeds)
      const orderData = {
        delivery_address: `${formData.streetAddress}, ${formData.postalCode} ${formData.city}`,
        customer_id: userId || null,
        delivery_date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
        delivery_notes: formData.notes,
        delivery_time: selectedSlot ? selectedSlot.start_time : null,
        guest_details: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          address: `${formData.streetAddress}, ${formData.postalCode} ${formData.city}`
        },
        items: orderItems.map(item => ({
          ...item,
          unit_price: Number(item.unit_price)
        })),
        total_amount: Number(calculatedTotal.toFixed(2))
      };

      // Update user profile if changed
      if (userId && user && (
        formData.firstName !== user?.user_metadata?.first_name ||
        formData.lastName !== user?.user_metadata?.last_name ||
        `${formData.streetAddress}, ${formData.postalCode} ${formData.city}` !== user?.user_metadata?.address ||
        formData.phone !== user?.user_metadata?.phone
      )) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: formData.firstName,
            last_name: formData.lastName,
            address: `${formData.streetAddress}, ${formData.postalCode} ${formData.city}`,
            phone: formData.phone
          })
          .eq('id', userId);

        if (profileError) throw profileError;
      }

      // T008: No longer storing orderDataForPayment in frontend state
      // Order data is stored in payment_authorizations metadata by authorize-payment function
      setShowPaymentForm(true);
    } catch (err: unknown) {
      logger.error('Checkout error:', err);
      let errorMessage = 'Failed to process your order. Please try again.';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // T010: Poll for order creation with exponential backoff
  // Webhook creates order asynchronously, so frontend needs to wait for it
  const pollForOrder = async (paymentIntentId: string, maxAttempts = 10): Promise<string | null> => {
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Query Supabase for order with this stripe_payment_intent_id
        const { data: order, error } = await supabase
          .from('orders')
          .select('id')
          .eq('stripe_payment_intent_id', paymentIntentId)
          .maybeSingle();

        if (error) {
          logger.error('Error polling for order:', error);
        }

        if (order) {
          logger.debug(`Order found after ${attempt + 1} attempts:`, order.id);
          return order.id;
        }

        // Exponential backoff: 500ms, 1s, 2s, 4s, 8s, 16s...
        const delayMs = 500 * Math.pow(2, attempt);
        logger.debug(`Order not found yet, waiting ${delayMs}ms before retry ${attempt + 1}/${maxAttempts}`);
        await delay(delayMs);
      } catch (error) {
        logger.error('Error during order polling:', error);
      }
    }

    return null; // Timeout after all attempts
  };

  // T009: Handle successful payment authorization (receives paymentIntentId, not orderIds)
  // T011: Added loading state during polling
  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      setIsPollingForOrder(true);
      setPaymentError(null);

      // Poll for order creation (webhook creates order asynchronously)
      const orderId = await pollForOrder(paymentIntentId);

      if (orderId) {
        // Order created successfully!
        clearCart();
        navigate(`/order-confirmation/${orderId}`);
      } else {
        // T014: Timeout error handling with retry option
        setPaymentError(
          'Betalningen är godkänd men ordern skapas fortfarande. ' +
          'Vänligen vänta några sekunder och uppdatera sidan, eller kontakta kundtjänst om problemet kvarstår.'
        );
      }
    } catch (err) {
      logger.error('Error after payment success:', err);
      setPaymentError('Betalningen genomförd men något gick fel vid orderbekräftelse. Kontakta kundtjänst.');
    } finally {
      setIsPollingForOrder(false);
    }
  };

  // Handle payment error
  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    logger.error('Payment error:', error);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold text-gray-900 mb-8" data-testid="checkout-heading">Checkout</h1>
        
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6" data-testid="error-message">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-y-6 lg:grid-cols-2 lg:gap-x-8">
          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    Förnamn
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Efternamn
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700">
                  Gatuadress
                </label>
                <input
                  type="text"
                  id="streetAddress"
                  name="streetAddress"
                  required
                  value={formData.streetAddress}
                  onChange={(e) => setFormData({ ...formData, streetAddress: e.target.value })}
                  placeholder="Gata och nummer"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                    Postnummer
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    required
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="123 45"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    Ort
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Göteborg"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  E-post
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>

              <div>
                <AutomaticDeliveryInfo onDeliverySlotAssigned={handleDeliverySlotAssigned} />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                  Leveransinstruktioner (Valfritt)
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Portkod, leveransinstruktioner, etc."
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                />
              </div>

              {/* Data storage consent */}
              <div className="mt-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="dataConsent"
                      name="dataConsent"
                      type="checkbox"
                      required
                      checked={formData.dataConsent}
                      onChange={(e) => setFormData({ ...formData, dataConsent: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="dataConsent" className="font-medium text-gray-700">
                      Hantering av personuppgifter
                    </label>
                    <p className="text-gray-500">
                      Jag godkänner att Lokalaproducenter sparar mina personuppgifter för att kunna leverera min beställning. 
                      Lokalaproducenter kommer inte att dela eller sälja mina uppgifter till tredje part.
                    </p>
                  </div>
                </div>
              </div>

              {/* Marketing consent */}
              <div className="mt-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="marketingConsent"
                      name="marketingConsent"
                      type="checkbox"
                      checked={formData.marketingConsent}
                      onChange={(e) => setFormData({ ...formData, marketingConsent: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="marketingConsent" className="font-medium text-gray-700">
                      Marknadsföring via e-post
                    </label>
                    <p className="text-gray-500">
                      Jag godkänner att Lokalaproducenter skickar information om nyheter och erbjudanden till min e-post. 
                      Detta är valfritt och jag kan när som helst avregistrera mig.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !selectedSlot || !selectedDate || !formData.dataConsent || isBelowMinimumOrder}
                className="w-full bg-green-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Bearbetar...' :
                  !selectedSlot ? 'Tilldelar leveranstid...' :
                  !formData.dataConsent ? 'Godkänn hantering av personuppgifter' :
                  isBelowMinimumOrder ? `Minsta beställning måste vara ${minOrderAmount} kr` :
                  'Fortsätt till betalning'}
              </button>
            </form>

            {/* Show payment form after order data is prepared */}
            {showPaymentForm && selectedDate && selectedSlot && (
              <div className="mt-8 p-6 bg-white rounded-lg border border-gray-200">
                <h2 className="text-lg font-medium text-gray-900 mb-4">Betalning</h2>
                {paymentError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-700">{paymentError}</p>
                  </div>
                )}
                {/* T011: Loading state during order polling */}
                {isPollingForOrder && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                      <p className="text-sm text-blue-700">Betalningen är godkänd! Skapar din order...</p>
                    </div>
                  </div>
                )}
                <DelayedOrderPaymentForm
                  cartItems={items.map(item => ({
                    id: item.id,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    producerId: item.producerId
                  }))}
                  customerEmail={formData.email}
                  deliveryDate={format(selectedDate, 'yyyy-MM-dd')}
                  deliveryTime={`${selectedSlot.start_time.slice(0, 5)} - ${selectedSlot.end_time.slice(0, 5)}`}
                  guestDetails={{
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    email: formData.email,
                    address: `${formData.streetAddress}, ${formData.postalCode} ${formData.city}`
                  }}
                  deliveryAddress={`${formData.streetAddress}, ${formData.postalCode} ${formData.city}`}
                  deliveryNotes={formData.notes}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Ordersammanfattning</h2>
            <div className="flow-root">
              <ul className="-my-4 divide-y divide-gray-200">
                {items.map((item) => (
                  <li key={item.id} className="flex py-4">
                    <div className="flex-1 ml-4">
                      <div className="flex justify-between">
                        <h3 className="text-sm text-gray-900">{item.name}</h3>
                        <p className="text-sm font-medium text-gray-900">
                          {(item.price * item.quantity).toLocaleString('sv-SE')} kr
                        </p>
                      </div>
                      <p className="text-sm text-gray-500">Antal: {item.quantity}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex justify-between text-base font-medium text-gray-900">
                  <p>Totalt</p>
                  <p>{total().toLocaleString('sv-SE')} kr</p>
                </div>
                
                {/* Minimum Order Warning */}
                {isBelowMinimumOrder && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-sm text-yellow-800">
                    <p>Minsta beställning måste vara {minOrderAmount.toLocaleString('sv-SE')} kr.</p>
                    <p>Du saknar {(minOrderAmount - cartTotal).toLocaleString('sv-SE')} kr för att nå minimibeloppet.</p>
                    <Link
                      to="/products"
                      className="mt-1 text-green-600 hover:text-green-500 font-medium inline-block"
                    >
                      Fortsätt handla
                    </Link>
                  </div>
                )}

                {/* Payment information */}
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    Betalning sker säkert via Stripe
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}